import {
  Blocks,
  Events,
  Extensions,
  defineBlocksWithJsonArray,
  type Block,
} from 'blockly/core';

import { BEGINNER_DISPLAY_BLOCK_STYLE } from './display-helpers.js';
import {
  DISPLAY_POSITION_LINE_COLUMN,
  TFT_HEIGHT,
  TFT_TEXT_SIZE_OPTIONS,
  displayColorField,
  tftRadiusField,
  tftSizeField,
  tftXField,
  tftYField,
} from './tft-fields.js';
import {
  createTftPositionModeDropdown,
  updateTftPositionDetails,
} from './tft-position.js';
import { registerBeginnerTftConfigureBlock } from './tft-configure-block.js';

const TFT_POSITION_UI_EXTENSION = 'beginner_tft_position_ui';

function registerTftPositionUiExtension(): void {
  if (Extensions.isRegistered(TFT_POSITION_UI_EXTENSION)) {
    return;
  }

  Extensions.register(TFT_POSITION_UI_EXTENSION, function (this: Block) {
    this.setOnChange((event) => {
      if (event.type === Events.FINISHED_LOADING) {
        queueMicrotask(() => updateTftPositionDetails(this, undefined, true));
        return;
      }

      if (
        event.type === Events.BLOCK_CHANGE &&
        'blockId' in event &&
        event.blockId === this.id &&
        'name' in event &&
        event.name === 'POSITION_MODE'
      ) {
        const newValue =
          'newValue' in event && event.newValue != null ? String(event.newValue) : undefined;
        queueMicrotask(() => {
          if (!this.isDisposed()) {
            updateTftPositionDetails(this, newValue, true);
          }
        });
      }
    });
  });
}

function applyTftStatementDefaults(block: Block, tooltip: string): void {
  block.setPreviousStatement(true, null);
  block.setNextStatement(true, null);
  block.setStyle(BEGINNER_DISPLAY_BLOCK_STYLE);
  block.setTooltip(tooltip);
  block.setHelpUrl('');
}

function registerTftPositionBlocks(): void {
  registerTftPositionUiExtension();

  if (!Blocks['beginner_tft_display_text']) {
    Blocks['beginner_tft_display_text'] = {
      init(this: Block) {
        this.appendValueInput('TEXT').setCheck('String').appendField('TFT display text');
        this.appendDummyInput('MODE_ROW')
          .appendField('at')
          .appendField(createTftPositionModeDropdown(this), 'POSITION_MODE');
        applyTftStatementDefaults(this, 'Shows text on the TFT at a line/column or x/y position.');
        Extensions.apply(TFT_POSITION_UI_EXTENSION, this, false);
        this.setFieldValue(DISPLAY_POSITION_LINE_COLUMN, 'POSITION_MODE');
        updateTftPositionDetails(this, DISPLAY_POSITION_LINE_COLUMN, true);
      },
    };
  }

  if (!Blocks['beginner_tft_set_cursor']) {
    Blocks['beginner_tft_set_cursor'] = {
      init(this: Block) {
        this.appendDummyInput('HEADER')
          .appendField('TFT set cursor')
          .appendField(createTftPositionModeDropdown(this), 'POSITION_MODE');
        applyTftStatementDefaults(
          this,
          'Moves the TFT cursor using line/column or x/y coordinates.',
        );
        Extensions.apply(TFT_POSITION_UI_EXTENSION, this, false);
        this.setFieldValue(DISPLAY_POSITION_LINE_COLUMN, 'POSITION_MODE');
        updateTftPositionDetails(this, DISPLAY_POSITION_LINE_COLUMN, true);
      },
    };
  }
}

/**
 * Beginner TFT (ST7735 128×160) blocks.
 * One display only — no device dropdown. Configure TFT handles board-aware SPI pins.
 */
const tftStaticBlockDefinitions = [
  {
    type: 'beginner_tft_clear',
    message0: 'TFT clear screen',
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Clears the TFT screen.',
    helpUrl: '',
  },
  {
    type: 'beginner_tft_fill_screen',
    message0: 'TFT fill screen %1',
    args0: [displayColorField('COLOR')],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Fills the entire TFT screen with a color.',
    helpUrl: '',
  },
  {
    type: 'beginner_tft_set_text_size',
    message0: 'TFT set text size %1',
    args0: [
      {
        type: 'field_dropdown',
        name: 'SIZE',
        options: TFT_TEXT_SIZE_OPTIONS,
      },
    ],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Sets how large TFT text appears (1–5).',
    helpUrl: '',
  },
  {
    type: 'beginner_tft_set_text_color',
    message0: 'TFT set text color %1',
    args0: [displayColorField('COLOR')],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Sets the color used for TFT text.',
    helpUrl: '',
  },
  {
    type: 'beginner_tft_draw_pixel',
    message0: 'TFT draw pixel x %1 y %2 color %3',
    args0: [tftXField('X'), tftYField('Y'), displayColorField()],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Draws one pixel on the TFT.',
    helpUrl: '',
  },
  {
    type: 'beginner_tft_draw_line',
    message0: 'TFT draw line from x %1 y %2 to x %3 y %4 color %5',
    args0: [
      tftXField('X1'),
      tftYField('Y1'),
      tftXField('X2', 40),
      tftYField('Y2', 40),
      displayColorField(),
    ],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Draws a line between two points on the TFT.',
    helpUrl: '',
  },
  {
    type: 'beginner_tft_draw_rectangle',
    message0: 'TFT draw rectangle x %1 y %2 width %3 height %4 color %5',
    args0: [
      tftXField('X'),
      tftYField('Y'),
      tftSizeField('WIDTH', 40),
      tftSizeField('HEIGHT', 30, TFT_HEIGHT),
      displayColorField(),
    ],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Draws a rectangle outline on the TFT.',
    helpUrl: '',
  },
  {
    type: 'beginner_tft_fill_rectangle',
    message0: 'TFT fill rectangle x %1 y %2 width %3 height %4 color %5',
    args0: [
      tftXField('X'),
      tftYField('Y'),
      tftSizeField('WIDTH', 40),
      tftSizeField('HEIGHT', 30, TFT_HEIGHT),
      displayColorField(),
    ],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Fills a rectangle on the TFT.',
    helpUrl: '',
  },
  {
    type: 'beginner_tft_draw_circle',
    message0: 'TFT draw circle center x %1 center y %2 radius %3 color %4',
    args0: [
      tftXField('CX', 64),
      tftYField('CY', 80),
      tftRadiusField('RADIUS', 20),
      displayColorField(),
    ],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Draws a circle outline on the TFT.',
    helpUrl: '',
  },
  {
    type: 'beginner_tft_fill_circle',
    message0: 'TFT fill circle center x %1 center y %2 radius %3 color %4',
    args0: [
      tftXField('CX', 64),
      tftYField('CY', 80),
      tftRadiusField('RADIUS', 20),
      displayColorField(),
    ],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Fills a circle on the TFT.',
    helpUrl: '',
  },
  {
    type: 'beginner_tft_draw_triangle',
    message0: 'TFT draw triangle x1 %1 y1 %2 x2 %3 y2 %4 x3 %5 y3 %6 color %7',
    args0: [
      tftXField('X1', 20),
      tftYField('Y1', 120),
      tftXField('X2', 100),
      tftYField('Y2', 120),
      tftXField('X3', 60),
      tftYField('Y3', 40),
      displayColorField(),
    ],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Draws a triangle outline on the TFT.',
    helpUrl: '',
  },
  {
    type: 'beginner_tft_fill_triangle',
    message0: 'TFT fill triangle x1 %1 y1 %2 x2 %3 y2 %4 x3 %5 y3 %6 color %7',
    args0: [
      tftXField('X1', 20),
      tftYField('Y1', 120),
      tftXField('X2', 100),
      tftYField('Y2', 120),
      tftXField('X3', 60),
      tftYField('Y3', 40),
      displayColorField(),
    ],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Fills a triangle on the TFT.',
    helpUrl: '',
  },
  {
    type: 'beginner_tft_draw_rounded_rectangle',
    message0: 'TFT draw rounded rectangle x %1 y %2 width %3 height %4 radius %5 color %6',
    args0: [
      tftXField('X'),
      tftYField('Y'),
      tftSizeField('WIDTH', 40),
      tftSizeField('HEIGHT', 30, TFT_HEIGHT),
      tftRadiusField('RADIUS', 8),
      displayColorField(),
    ],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Draws a rounded rectangle outline on the TFT.',
    helpUrl: '',
  },
  {
    type: 'beginner_tft_fill_rounded_rectangle',
    message0: 'TFT fill rounded rectangle x %1 y %2 width %3 height %4 radius %5 color %6',
    args0: [
      tftXField('X'),
      tftYField('Y'),
      tftSizeField('WIDTH', 40),
      tftSizeField('HEIGHT', 30, TFT_HEIGHT),
      tftRadiusField('RADIUS', 8),
      displayColorField(),
    ],
    previousStatement: null,
    nextStatement: null,
    style: BEGINNER_DISPLAY_BLOCK_STYLE,
    tooltip: 'Fills a rounded rectangle on the TFT.',
    helpUrl: '',
  },
];

export function registerBeginnerTftBlocks(): void {
  registerBeginnerTftConfigureBlock();
  registerTftPositionBlocks();
  defineBlocksWithJsonArray(tftStaticBlockDefinitions);
}
